import axios from 'axios';
import pRetry, { AbortError } from 'p-retry';
import { navigate } from '../navigation';
import { parseApiError } from '../utils/errors';
import { isTokenExpired, clearAuthData } from '../utils/auth';
import { getAccessToken, setAccessToken } from '../utils/accessToken';

const api = axios.create({
  baseURL: process.env.REACT_APP_WALLET_API_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise = null;

const getCookie = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }
  const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
  const target = cookies.find((cookie) => cookie.startsWith(`${name}=`));
  return target ? decodeURIComponent(target.split('=').slice(1).join('=')) : '';
};

const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const csrfToken = getCookie('XSRF-TOKEN');
      const response = await api.post('/user/token/refresh', {}, {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });

      const { Token, User } = response.data || {};
      if (Token) {
        setAccessToken(Token);
      }
      if (User) {
        localStorage.setItem('user', JSON.stringify(User));
      }
      return Token;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      if (isTokenExpired(token)) {
        setAccessToken('');
        return config;
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isRefreshRequest = originalRequest?.url?.includes('/user/token/refresh');
    const isLogoutRequest = originalRequest?.url?.includes('/user/logout');
    if (error.response && error.response.status === 401 && !originalRequest?._retry && !isRefreshRequest && !isLogoutRequest) {
      originalRequest._retry = true;
      try {
        await refreshAccessToken();
        const token = getAccessToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        clearAuthData();
        navigate('/login');
        return Promise.reject(refreshError);
      }
    }
    if (error.response && error.response.status === 401) {
      clearAuthData();
      navigate('/login');
    }
    const apiError = parseApiError(error);
    error.apiError = apiError;
    return Promise.reject(error);
  }
);

function createAbortErrorWithOriginal(originalError) {
  const abortError = new AbortError(originalError.apiError?.message || originalError.message);
  abortError.originalError = originalError;
  abortError.apiError = originalError.apiError;
  abortError.response = originalError.response;
  return abortError;
}

const RETRY_CONFIG = {
  retries: 3,
  minTimeout: 1000,
  maxTimeout: 2000,
};

const createRetryableRequest = (requestFn) => {
  return pRetry(() => {
    return requestFn().catch((error) => {
      if (error.response && error.response.status < 500) {
        throw createAbortErrorWithOriginal(error);
      }
      throw error;
    });
  }, RETRY_CONFIG);
};

const retryableApi = {
  get: (url, config) => createRetryableRequest(() => api.get(url, config)),
  post: (url, data, config) => createRetryableRequest(() => api.post(url, data, config)),
  put: (url, data, config) => createRetryableRequest(() => api.put(url, data, config)),
  delete: (url, config) => createRetryableRequest(() => api.delete(url, config)),
  refresh: () => refreshAccessToken(),
  logout: () => api.post('/user/logout', {}, {
    withCredentials: true,
    headers: {
      'X-CSRF-Token': getCookie('XSRF-TOKEN'),
    },
  }),
};

// Positions v2 API functions (single-currency, flat response shape)
export const getPositionsV2 = (currency, params) =>
  retryableApi.get('/v2/positions', { params: { ...params, currency } });

export const getPositionsByAssetClassV2 = (currency, params) =>
  retryableApi.get('/v2/positions/asset-class', { params: { ...params, currency } });

export const getPositionsByInstrumentTypeV2 = (currency, params) =>
  retryableApi.get('/v2/positions/instrument-type', { params: { ...params, currency } });

export const getPositionIncomeHistoryV2 = (id, currency, params) =>
  retryableApi.get(`/v2/positions/${id}/income/history`, { params: { ...params, currency } });

// Correlation API functions
export const createCorrelation = (parentId, childId) =>
  retryableApi.post(`/cashout/${parentId}/correlations`, { child_expense_id: childId });

export const getCorrelations = (parentId) =>
  retryableApi.get(`/cashout/${parentId}/correlations`);

export const deleteCorrelation = (parentId, childId) =>
  retryableApi.delete(`/cashout/${parentId}/correlations/${childId}`);

export const getCorrelationCandidates = (params) =>
  retryableApi.get('/cashout/correlation-candidates', { params });

// Annual budget API functions
export const getAnnualBudget = (year) =>
  retryableApi.get('/budget/annual', { params: { year } });

export const setBudgetCell = (payload) =>
  retryableApi.put('/budget/cell', payload);

export const linkExpenseToRecurring = (cashOutId, recurringExpenseId) =>
  retryableApi.put('/budget/link', { CashOutId: cashOutId, RecurringExpenseId: recurringExpenseId });

export const getLinkSuggestions = () =>
  retryableApi.get('/budget/link/suggestions');

export const getRecurringExpenses = () =>
  retryableApi.get('/recurring-expenses');

export const createRecurringExpense = (payload) =>
  retryableApi.post('/recurring-expenses', payload);

export const updateRecurringExpense = (payload) =>
  retryableApi.put('/recurring-expenses', payload);

export const deleteRecurringExpense = (id) =>
  retryableApi.delete(`/recurring-expenses/${id}`);

export default retryableApi;